import {
  InteractiveFloorPlan,
  type FloorTableInfo,
} from '@/features/reservation-setup/components/InteractiveFloorPlan';
import {
  useFloorPlan,
  useTableMode,
  useZones,
} from '@/features/reservation-setup/hooks/useReservationSetup';
import { useT } from '@/i18n';
import { formatMoney } from '@/utils/money';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { TableState } from '@restaurant/api-client';
import { Block, Preloader } from 'framework7-react';
import { useTableStates } from '../hooks/useTableOrdering';
import { STATE_STYLE } from '../lib/tableState';
import TableGridView from './TableGridView';

/**
 * The live floor - the actual saved plan on the SAME interactive canvas the
 * reservations plan and the editor use (real pan / zoom / pinch / fit / zone
 * picker). Tables keep their editor figure; live table-ordering state is a
 * thin overlay (outline accent + corner dot + open total + service badge).
 *
 * A floor plan is an ADVANCED-table-mode concept (mirrors TablePlanPage): in
 * SIMPLE mode, or ADVANCED with nothing drawn yet, it falls back to the
 * grouped grid so the screen is never empty or a lie.
 */
export default function FloorPlanLiveView({
  activeZoneId,
  selectedId,
  onSelect,
  topInset = 0,
  bottomInset = 0,
}: {
  activeZoneId: string | null;
  selectedId: string | null;
  onSelect: (table: TableState) => void;
  /** Heights (px) of the navbar+subnavbar / bottom tab bar the canvas runs
   *  under, so the fit keeps the plan in the clear band between them while
   *  dragged content slides under the bars (see InteractiveFloorPlan). */
  topInset?: number;
  bottomInset?: number;
}) {
  const t = useT().tableOrdering.floorPlan;
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';
  const { data: tableMode, isLoading: modeLoading } = useTableMode();
  const { data: floorPlan, isLoading: planLoading } = useFloorPlan();
  const { data: zones, isLoading: zonesLoading } = useZones();
  const { data: states, isLoading: statesLoading } = useTableStates();

  if (modeLoading || planLoading || zonesLoading || statesLoading) {
    return (
      <Block style={{ textAlign: 'center', paddingTop: 48 }}>
        <Preloader size={32} />
      </Block>
    );
  }

  const tableEls = (floorPlan?.layout.elements ?? []).filter((e) => e.kind === 'table');
  const isAdvanced = tableMode?.tableMode === 'ADVANCED';

  // SIMPLE mode, or ADVANCED with no plan drawn → grouped grid (scrolls
  // normally in .rsv-main; never the empty/fake canvas).
  if (!isAdvanced || !floorPlan || tableEls.length === 0) {
    return (
      <>
        <Block style={{ margin: '12px 16px 0', fontSize: 13, color: '#8e8e93' }}>
          {isAdvanced ? t.noFloorPlanAdvanced : t.simpleMode}
        </Block>
        <TableGridView
          mode="MAP"
          activeZoneId={activeZoneId}
          selectedId={selectedId}
          onSelect={onSelect}
        />
      </>
    );
  }

  const byId = new Map((states ?? []).map((s) => [s.tableId, s]));
  const tableInfo = new Map<string, FloorTableInfo>(
    (states ?? []).map((s) => [s.tableId, { label: s.label, seats: s.seats, zoneId: s.zoneId }]),
  );
  const zonesArr = (zones ?? []).map((z) => ({
    id: z.id,
    label: z.label,
    sortOrder: z.sortOrder ?? 0,
  }));

  return (
    <div className="rsv-plan rsv-plan-advanced">
      <InteractiveFloorPlan
        elements={floorPlan.layout.elements}
        tableInfo={tableInfo}
        zones={zonesArr}
        canvas={floorPlan.layout.canvas}
        activeZoneId={activeZoneId}
        selectedTableId={selectedId}
        topInset={topInset}
        bottomInset={bottomInset}
        onTableClick={(tableId) => {
          const st = byId.get(tableId);
          if (st) onSelect(st);
        }}
        accentFor={(tableId) => {
          const st = byId.get(tableId);
          return st && st.state !== 'AVAILABLE' ? STATE_STYLE[st.state].dot : undefined;
        }}
        renderOverlay={(tableId, el) => {
          const st = byId.get(tableId);
          if (!st) return null;
          const style = STATE_STYLE[st.state];
          return (
            <>
              {st.state === 'OCCUPIED' && (
                <text
                  x={el.width / 2}
                  y={el.height / 2 + 14}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={11}
                  fontWeight={700}
                  fill="#1e293b"
                  fontFamily="system-ui, -apple-system, sans-serif"
                >
                  {formatMoney(st.totalCents, currency)}
                </text>
              )}
              <circle
                cx={el.width - 9}
                cy={9}
                r={5}
                fill={style.dot}
                stroke="#fff"
                strokeWidth={1.5}
              />
              {st.pendingServiceRequests > 0 && (
                <g transform="translate(-1 -1)">
                  <circle r={10} fill="#ff3b30" stroke="#fff" strokeWidth={2} />
                  <text
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={11}
                    fontWeight={700}
                    fill="#fff"
                  >
                    {st.pendingServiceRequests}
                  </text>
                </g>
              )}
            </>
          );
        }}
      />
    </div>
  );
}
